// pages/Cargos.js
import React, { useState, useEffect } from 'react';
import {
  Table, Spin, 
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Card,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ProductOutlined,
  ExportOutlined,
  BarcodeOutlined,
  FileTextOutlined,
  PercentageOutlined,
  ShoppingOutlined,
  CalculatorOutlined,
  TagOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;

const Cargos = () => {
  // Estados
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentCargo, setCurrentCargo] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // Estados para datos relacionados
  const [secciones, setSecciones] = useState([]);
  const [loadingSecciones, setLoadingSecciones] = useState(false);
  const [tiposIva, setTiposIva] = useState([]);
  const [loadingTiposIva, setLoadingTiposIva] = useState(false);

  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});


  // Configuración de columnas de la tabla
  const columns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 100,
      sorter: (a, b) => a.codigo?.localeCompare(b.codigo || '') || 0,
      sortOrder: sortedInfo.columnKey === 'codigo' && sortedInfo.order,
      render: (codigo) => (
        <Tag 
          color="blue" 
          style={{ 
            fontWeight: 'bold', 
            fontSize: '13px',
            padding: '4px 8px'
          }}
        >
          {/* <BarcodeOutlined style={{ marginRight: 4 }} /> */}
          {codigo}
        </Tag>
      ),
      fixed: 'left',
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 200,
      sorter: (a, b) => a.descripcion?.localeCompare(b.descripcion || '') || 0,
      sortOrder: sortedInfo.columnKey === 'descripcion' && sortedInfo.order,
      render: (descripcion) => (
        <div style={{ maxWidth: 200 }}>
          <Text strong style={{ fontSize: '13px' }}>
            {descripcion}
          </Text>
        </div>
      ),
    },
    {
      title: 'Sección',
      dataIndex: 'seccion',
      key: 'seccion',
      width: 120,
      sorter: (a, b) => a.seccion?.localeCompare(b.seccion || '') || 0,
      sortOrder: sortedInfo.columnKey === 'seccion' && sortedInfo.order,
      render: (seccionCodigo, record) => {
        const seccion = secciones.find(s => s.Codigo === seccionCodigo);
        return (
          <Tooltip title={seccion ? seccion.Descripcion : 'Sección no encontrada'}>
            <Tag color="purple" style={{ cursor: 'help' }}>
              {seccionCodigo}
            </Tag>
          </Tooltip>
        );
      },
      filters: [],
      filteredValue: filteredInfo.seccion || null,
      onFilter: (value, record) => record.seccion === value,
    },
    {
      title: 'Tipo IVA',
      dataIndex: 'codigoiva',
      key: 'codigoiva',
      width: 100,
      sorter: (a, b) => a.codigoiva?.localeCompare(b.codigoiva || '') || 0,
      sortOrder: sortedInfo.columnKey === 'codigoiva' && sortedInfo.order,
      render: (codigoiva, record) => {
        if (tiposIva){
          const tipoIva = tiposIva.find(t => t.Codigo === codigoiva);
          return (
            <Tooltip title={tipoIva ? `${tipoIva.Descripcion} (${tipoIva.ValorActual}%)` : 'Tipo IVA no encontrado'}>
              <Tag color="orange" style={{ cursor: 'help' }}>
                {tipoIva.Descripcion} {tipoIva.ValorActual}%
              </Tag>
            </Tooltip>
          );
        }
      },
      filters: [],
      filteredValue: filteredInfo.codigoiva || null,
      onFilter: (value, record) => record.codigoiva === value,
    },
    {
      title: 'Precio',
      dataIndex: 'precio',
      key: 'precio',
      width: 120,
      sorter: (a, b) => (a.precio || 0) - (b.precio || 0),
      sortOrder: sortedInfo.columnKey === 'precio' && sortedInfo.order,
      render: (precio) => (
        precio !== undefined ? (
          <Text strong style={{ fontSize: '13px' }}>
            {precio.toFixed(2)} €
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: '12px' }}>
            N/A
          </Text>
        )
      ),
    },
    {
      title: 'Calcula dto',
      dataIndex: 'calculadto',
      key: 'calculadto',
      width: 110,
      render: (calculadto) => (
        calculadto ? (
          <Badge
            status="success"
            text="Sí"
            style={{ color: '#52c41a', fontWeight: 'bold' }}
          />
        ) : (
          <Badge
            status="default"
            text="No"
            style={{ color: '#d9d9d9', fontWeight: 'bold' }}
          />
        )
      ),
      filters: [
        { text: 'Sí', value: true },
        { text: 'No', value: false },
      ],
      filteredValue: filteredInfo.calculadto || null,
      onFilter: (value, record) => record.calculadto === value,
    },
    {
      title: 'Activo',
      dataIndex: 'activo',
      key: 'activo',
      width: 100,
      render: (activo) => (
        activo ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
        )
      ),
      filters: [
        { text: 'Sí', value: true },
        { text: 'No', value: false },
      ],
      filteredValue: filteredInfo.activo || null,
      onFilter: (value, record) => record.activo === value,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar cargo?"
            description="Esta acción eliminará el cargo permanentemente"
            onConfirm={() => handleDelete(record.codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchCargos();
    fetchSecciones();
    fetchTiposIva();
  }, []);

  // Cargar datos de secciones
  useEffect(() => {
    if (secciones.length > 0) {
      const seccionFilters = secciones.map(seccion => ({
        text: `${seccion.Codigo} - ${seccion.Descripcion}`,
        value: seccion.Codigo
      }));
      
      // Actualizar filtros de sección en columnas
      columns.find(col => col.key === 'seccion').filters = seccionFilters;
    }
  }, [secciones]);

  // Cargar datos de tipos de IVA
  useEffect(() => {
    if (tiposIva.length > 0) {
      const tipoIvaFilters = tiposIva.map(tipo => ({
        text: `${tipo.codigo} - ${tipo.descripcion} (${tipo.porcentaje}%)`,
        value: tipo.codigo
      }));
      
      // Actualizar filtros de tipo IVA en columnas
      columns.find(col => col.key === 'codigoiva').filters = tipoIvaFilters;
    }
  }, [tiposIva]);

  const fetchCargos = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Parámetros de la query
      const queryParams = {
        search: searchText,
        sortField: params.sortField || sortedInfo.field || 'codigo',
        sortOrder: params.sortOrder || (sortedInfo.order === 'descend' ? 'DESC' : 'ASC')
      };
      
      // Añadir filtros
      if (filteredInfo.seccion) {
        queryParams.seccion = filteredInfo.seccion[0];
      }
      if (filteredInfo.codigoiva) {
        queryParams.codigoiva = filteredInfo.codigoiva[0];
      }
      if (filteredInfo.calculadto) {
        queryParams.calculadto = filteredInfo.calculadto[0];
      }
      if (filteredInfo.activo) {
        queryParams.activo = filteredInfo.activo[0];
      }
      
      const response = await axios.get(`${BASE_URL}/api/cargos`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      const cleanedData = (response.data.data || []).map(item => ({
        ...item,
        descripcion: item.descripcion || '',
        seccion: item.seccion || '',
        codigoiva: item.codigoiva || '',
        precio: item.precio !== undefined ? parseFloat(item.precio) : undefined,
        calculadto: Boolean(item.calculadto),
        activo: Boolean(item.activo)
      }));
      
      setCargos(cleanedData);
      
      
    } catch (error) {
      console.error('❌ Error cargando cargos:', error);
      message.error('Error al cargar los cargos');
      setCargos([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchSecciones = async () => {
    setLoadingSecciones(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/secciones`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      
      
      // Ordenar por descripción
      const seccionesOrdenadas = (response.data.data || [])
        .sort((a, b) => a.Descripcion.localeCompare(b.Descripcion));    
    
      
      setSecciones(seccionesOrdenadas);
    } catch (error) {
      console.error('❌ Error cargando secciones:', error);
      message.error('Error al cargar las secciones');
      setSecciones([]);
    } finally {
      setLoadingSecciones(false);
    }
  };

  const fetchTiposIva = async () => {
    setLoadingTiposIva(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/tiposiva`, {
        headers: { Authorization: `Bearer ${token}` }
      });
            
      
    //   // Ordenar por código
      const tiposIvaOrdenados = (response.data.data || [])
        .sort((a, b) => a.Codigo.localeCompare(b.Codigo));
      
      // console.log(" tiposIvaOrdenados => ",tiposIvaOrdenados);
      

      setTiposIva(tiposIvaOrdenados);
    } catch (error) {
      console.error('❌ Error cargando tipos de IVA:', error);
      message.error('Error al cargar los tipos de IVA');
      setTiposIva([]);
    } finally {
      setLoadingTiposIva(false);
    }
  };

  

  // Handlers para las acciones
  const handleCreate = async () => {
    setModalType('create');
    setCurrentCargo(null);
    form.resetFields();
    
    // Establecer valores por defecto
    form.setFieldsValue({
      calculadto: true,
      activo: true,
      precio: 0
    });
    
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentCargo(record);
    
    const formValues = {
      codigo: record.codigo,
      descripcion: record.descripcion || '',
      seccion: record.seccion || '',
      codigoiva: record.codigoiva || '',
      precio: record.precio || 0,
      calculadto: record.calculadto || true,
      activo: record.activo || true
    };
    
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleView = (record) => {
    const seccion = secciones.find(s => s.Codigo === record.seccion);
    const tipoIva = tiposIva.find(t => t.Codigo === record.codigoiva);
    
    Modal.info({
      title: `Cargo ${record.codigo}`,
      width: 700,
      icon: <ProductOutlined />,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <strong>Código:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue" style={{ fontSize: '16px', padding: '6px 12px' }}>
                  {/* <BarcodeOutlined style={{ marginRight: 8 }} /> */}
                  {record.codigo}
                </Tag>
              </div>
            </Col>
            
            <Col span={24}>
              <strong>Descripción:</strong>
              <div style={{ 
                marginTop: 4, 
                padding: '12px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '6px',
                fontSize: '14px'
              }}>
                {record.descripcion || 'Sin descripción'}
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Sección:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="purple" style={{ fontSize: '14px', padding: '4px 8px' }}>
                  {record.seccion}
                </Tag>
                {seccion && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
                    {seccion.Descripcion}
                  </div>
                )}
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Tipo IVA:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="orange" style={{ fontSize: '14px', padding: '4px 8px' }}>
                  {record.codigoiva}
                </Tag>
                {tipoIva && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
                    {tipoIva.Descripcion} ({tipoIva.ValorActual}%)
                  </div>
                )}
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Precio:</strong>
              <div style={{ marginTop: 4 }}>
                {record.precio !== undefined ? (
                  <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                    {record.precio.toFixed(2)} €
                  </Text>
                ) : (
                  <Text type="secondary">No definido</Text>
                )}
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Calcula dto:</strong>
              <div style={{ marginTop: 4 }}>
                {record.calculadto ? (
                  <Badge 
                    status="success" 
                    text="Sí" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                ) : (
                  <Badge 
                    status="default" 
                    text="No" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                )}
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Activo:</strong>
              <div style={{ marginTop: 4 }}>
                {record.activo ? (
                  <Badge 
                    status="success" 
                    text="Sí" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                ) : (
                  <Badge 
                    status="error" 
                    text="No" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                )}
              </div>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (codigo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/cargos/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filtrar el eliminado
      setCargos(cargos.filter(p => p.codigo !== codigo));
      
      
      message.success('Cargo eliminado correctamente');
    } catch (error) {
      console.error('❌ Error eliminando cargo:', error);
      message.error(error.response?.data?.error || 'Error al eliminar el cargo');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');

      if (modalType === 'create') {
        const response = await axios.post(
          `${BASE_URL}/api/cargos`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchCargos();
        message.success('Cargo creado correctamente');
      } else {
        const response = await axios.put(
          `${BASE_URL}/api/cargos/${currentCargo.codigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Actualizar el estado local
        const CargoActualizado = {
          ...response.data.data,
          ...values
        };
        
        setCargos(prevCargos => 
          prevCargos.map(cargo => 
            cargo.codigo === currentCargo.codigo
              ? CargoActualizado 
              : cargo
          )
        );
        
        message.success('Cargo actualizado correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      console.error('❌ Error guardando cargo:', error);
      message.error(error.response?.data?.error || 'Error al guardar los datos');
    }
  };

  const handleSearch = () => {
    if (searchText.trim() === '') {
      message.warning('Por favor ingrese un término de búsqueda');
      return;
    }
    setSearchLoading(true);    
    fetchCargos({ current: 1 });
  };

  const handleResetSearch = () => {
    setSearchText('');
    setFilteredInfo({});
    setSortedInfo({});
    setSearchLoading(false);
    fetchCargos({ current: 1 });
  };

  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input
          placeholder="Buscar por código, descripción, sección..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 350 }}
          onPressEnter={handleSearch}
          allowClear
          onClear={handleResetSearch}
          prefix={<SearchOutlined />}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={searchLoading}
        >
          Buscar
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleResetSearch}
        >
          Restablecer
        </Button>
      </Space>
    </div>
  );

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Cargos" 
          value={cargos.length}
          prefix={<ProductOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Activos" 
          value={cargos.filter(p => p.activo).length}
          suffix={`/ ${cargos.length}`}
          prefix={<CheckCircleOutlined />}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Calcula dto" 
          value={cargos.filter(p => p.calculadto).length}
          suffix={`/ ${cargos.length}`}
          prefix={<CalculatorOutlined />}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
    </Row>
  );

  return (
    <>
      <PageContainer
        title="Cargos/Servicios"
        subtitle={`${cargos.length} cargos registrados`}
        icon={<ProductOutlined />}
        loading={loading}
        onRefresh={() => fetchCargos()}
        onCreate={handleCreate}
        badgeCount={cargos.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              Exportar
            </Button>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}
        
        {/* Barra de búsqueda */}
        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={cargos}
          rowKey={(record) => record.codigo}
          loading={loading}
          scroll={{ x: 1500, y: 600 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          locale={{
            emptyText: 'No se encontraron cargos'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar cargo */}
      <Modal
        title={modalType === 'create' ? 'Nuevo cargo' : 'Editar cargo'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={700}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            calculadto: true,
            activo: true,
            precio: 0
          }}
        >
          {/* Primera fila: Código y Descripción */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="codigo"
                label="Código"
                rules={[
                  { 
                    required: true, 
                    message: 'Por favor ingrese el código',
                    max: 10,
                    whitespace: false
                  }
                ]}
                tooltip="Código único del cargo/servicio"
              >
                <Input 
                  placeholder="Ej: ALQ, BAR, CEN"
                  maxLength={10}
                  disabled={modalType === 'edit'}
                //   prefix={<BarcodeOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="descripcion"
                label="Descripción"
                rules={[
                  { 
                    required: true, 
                    message: 'Por favor ingrese la descripción',
                    max: 100
                  }
                ]}
                tooltip="Descripción completa del cargo/servicio"
              >
                <Input 
                  placeholder="Descripción del cargo"
                  maxLength={100}
                  showCount
                  prefix={<FileTextOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Segunda fila: Sección y Tipo IVA */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="seccion"
                label="Sección"
                rules={[
                  { 
                    required: true, 
                    message: 'Por favor seleccione la sección'
                  }
                ]}
                tooltip="Sección contable a la que pertenece el cargo"
              >
                <Select
                  placeholder="Seleccione sección"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  loading={loadingSecciones}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  notFoundContent={loadingSecciones ? <Spin size="small" /> : "No hay secciones"}
                >
                  {secciones.map(seccion => (
                    <Option 
                      key={seccion.Codigo} 
                      value={seccion.Codigo}
                    >
                      {seccion.Codigo} - {seccion.Descripcion}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="codigoiva"
                label="Tipo IVA"
                rules={[
                  { 
                    required: true, 
                    message: 'Por favor seleccione el tipo de IVA'
                  }
                ]}
                tooltip="Tipo de IVA aplicable"
              >
                <Select
                  placeholder="Seleccione tipo IVA"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  loading={loadingTiposIva}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  notFoundContent={loadingTiposIva ? <Spin size="small" /> : "No hay tipos de IVA"}
                >
                  {tiposIva.map(tipo => (
                    <Option 
                      key={tipo.Codigo} 
                      value={tipo.Codigo}
                    >
                      {tipo.Codigo} - {tipo.Descripcion} ({tipo.ValorActual}%)
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Tercera fila: Precio */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="precio"
                label="Precio"
                rules={[
                  { 
                    type: 'number',
                    min: 0,
                    message: 'El precio no puede ser negativo'
                  }
                ]}
                tooltip="Precio unitario del cargo"
              >
                <InputNumber 
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  placeholder="0.00"
                  formatter={value => `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/€\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              {/* Espacio para alineación */}
            </Col>
          </Row>

          {/* Cuarta fila: Switches */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="calculadto"
                label="Calcula dto"
                valuePropName="checked"
                tooltip="Indica si se aplica dto al precio"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                  style={{ minWidth: 80 }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="activo"
                label="Activo"
                valuePropName="checked"
                tooltip="Indica si el cargo está activo en el sistema"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                  style={{ minWidth: 80 }}
                />
              </Form.Item>
            </Col>
          </Row>

          {modalType === 'create' && (
            <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>
                <strong>📝 Información importante:</strong>
                <ul style={{ marginTop: 8, marginBottom: 4, paddingLeft: 20 }}>
                  <li><strong>Código:</strong> Identificador único del cargo (ej: ALQ, BAR, CEN)</li>
                  <li><strong>Sección:</strong> Sección contable para clasificación y reportes</li>
                  <li><strong>Tipo IVA:</strong> Determina el porcentaje de IVA aplicable</li>
                  <li><strong>Precio:</strong> Precio unitario (0 si no aplica)</li>
                  <li><strong>Calcula dto:</strong> Activar si el precio se calcula con descuento</li>
                  <li><strong>Activo:</strong> Desactivar para ocultar el cargo en el sistema</li>
                </ul>
                <div style={{ marginTop: 8 }}>
                  <strong>💡 Ejemplos comunes:</strong>
                  <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
                    <li><strong>HAB</strong> - Habitación (sección HOT)</li>
                    <li><strong>MIN</strong> - Minibar (sección MIN)</li>
                    <li><strong>DES</strong> - Desayuno extra (sección PEN, valor negativo)</li>
                    <li><strong>CEN</strong> - Cena extra (sección PEN)</li>
                    <li><strong>ALQ</strong> - Alquiler bicicletas (sección VAR)</li>
                  </ul>
                </div>
              </div>
            </Card>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default Cargos;